import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import type { OfferRecord, Person } from '@vigeo/shared';
import { fetchOfferPreview, fetchOfferPreviewBytes } from '@/features/hr-workflow';
import { PdfFormPortal } from '@/steps/pdf-form/PdfFormPortal.js';

const useStyles = makeStyles({
  portal: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusLarge,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, overflow: 'hidden',
  },
  portalHead: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM, padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  portalBrand: { color: tokens.colorBrandForeground1, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
  step: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, padding: tokens.spacingHorizontalXL, minWidth: 0 },
  greeting: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  actions: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  /**
   * The candidate's buttons, drawn rather than rendered. They belong to the portal this is showing
   * a picture of; as real disabled buttons they were stand-ins a Viewer could count.
   *
   * `colorNeutralForeground3`, NOT `colorNeutralForegroundDisabled`: axe does not exempt text that
   * merely imitates a disabled control, and the disabled grey measured 1.65:1 on the muted fill.
   */
  portalGhost: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    paddingInline: tokens.spacingHorizontalL, paddingBlock: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
  },
  portalGhostPrimary: { backgroundColor: tokens.colorNeutralBackgroundDisabled },
  letter: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
});

/**
 * The offer letter as the portal will serve it, read-only — and, unless `letterOnly`, inside the
 * portal's own frame as the candidate sees it (frame 06b `295:801`).
 *
 * The page asks the server for the very artifact the issue path files and puts it through
 * `PdfFormPortal`, the same component the portal runs. A preview drawn from the typed terms
 * instead would let a reviewer approve something other than what goes out. The query key is the
 * one Review offer uses, so the two share one fetch.
 */
export function PortalPreview({ person, record, letterOnly = false }: {
  readonly person: Person;
  readonly record: OfferRecord;
  /** Just the letter, for the View PDF pop-up. */
  readonly letterOnly?: boolean;
}) {
  const styles = useStyles();
  const id = person.id;
  const recordId = record.id;
  const first = person.name.preferredName || person.name.givenName;
  const preview = useQuery({
    queryKey: ['offer-preview', id, recordId, record.revision],
    queryFn: () => fetchOfferPreview(id, recordId),
    retry: false,
  });
  // Memoised: a fresh object each render would restart the viewer's load effect every render.
  const template = useMemo(
    () => preview.data === undefined ? undefined : { version: preview.data.version, load: () => fetchOfferPreviewBytes(id, recordId) },
    [preview.data, id, recordId],
  );
  const noop = useCallback(() => {}, []);
  const rejectUpload = useCallback(async () => ({ ok: false as const, message: 'This is a preview. Nothing can be uploaded from it.' }), []);

  const letter = (
    <div className={styles.letter}>
      {preview.isPending ? <Spinner size="tiny" label="Preparing the offer letter" labelPosition="after" /> : null}
      {/* #261: the reviewer is TOLD. An offer whose letter cannot be built is the case
          that would otherwise render as an empty frame with a signing button under it. */}
      {preview.isError
        ? <MessageBar intent="error"><MessageBarBody>{(preview.error as Error).message}</MessageBarBody></MessageBar>
        : null}
      {preview.data === undefined ? null : (
        <PdfFormPortal
          config={preview.data.config}
          draft={undefined}
          onDraftChange={noop}
          // HR is reading the candidate's form, not filling it in. The overlay inputs are the
          // candidate's to complete, and nothing here can save them.
          disabled
          template={template}
          // Required by the contract, and unreachable here: a pdf-form step stages no file, and
          // every control is disabled. It refuses rather than pretending.
          upload={rejectUpload}
        />
      )}
    </div>
  );
  if (letterOnly) return letter;
  return (
    <div className={styles.portal}>
      <div className={styles.portalHead}>
        <span className={styles.portalBrand}>Vigeo Home Health</span>
        <Caption1>Secure onboarding</Caption1>
      </div>
      <div className={styles.step}>
        <h3 className={styles.greeting}>Hello {first}</h3>
        <Text>Work through these in any order. Everything saves as you go.</Text>
        <Caption1>1 of 1</Caption1>
        {letter}
        <div className={styles.actions} aria-hidden="true">
          <span className={styles.portalGhost}>Back</span>
          <span className={mergeClasses(styles.portalGhost, styles.portalGhostPrimary)}>Send this to our office</span>
        </div>
        <Caption1>Finish this step above and the button will turn on.</Caption1>
      </div>
    </div>
  );
}
